import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownProps } from 'react-bootstrap';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { FilterEntityOption } from 'v2/models';
import styles from './multiSelectFilter.module.scss';

interface MultiSelectFilterProps {
  label?: string;
  error?: string | null;
  className?: string;
  required?: boolean;
  options: FilterEntityOption[];
  name: string;
  // We don't know what are the form fields for this control
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  selectedValues: FilterEntityOption[];
  autoSetHeight?: boolean;
  drop?: DropdownProps['drop'];
  // We don't know what are the form fields for this control
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
}

const MultiSelectFilter = ({
  className,
  label,
  error,
  required,
  options,
  name,
  selectedValues,
  setValue,
  autoSetHeight = true,
  drop = 'up',
  control,
}: MultiSelectFilterProps) => {
  const [show, setShow] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(0);
  const dropDownRef = React.useRef<HTMLDivElement>(null);
  const dropMenuDownRef = React.useRef<HTMLDivElement>(null);

  const getSelectedItemIndex = (option: FilterEntityOption) => {
    const itemIndex = selectedValues.findIndex(
      (item) => item.name.toLowerCase() === option.name.toLowerCase()
    );
    return itemIndex;
  };

  const clickListener = React.useCallback(
    (e: MouseEvent) => {
      // This happens when user clicks outside the SearchBar component
      if (show && !dropDownRef.current?.contains(e.target as Node)) {
        setShow(false);
      }
    },
    [show]
  );

  React.useEffect(() => {
    document.addEventListener('click', clickListener);

    return () => {
      document.removeEventListener('click', clickListener);
    };
  }, [clickListener]);

  useEffect(() => {
    if (autoSetHeight && dropMenuDownRef.current && show) {
      const navBar = document
        .getElementById('topNavBar')
        ?.getBoundingClientRect();
      const navBarHeight = navBar?.height || 100;
      const menu = dropMenuDownRef.current.getBoundingClientRect();
      const menuBottomPosition = menu.top;
      const heightAdjust = 25; // Magic number to keep a space between the top navbar and dropdown
      setMenuMaxHeight(
        Math.max(menuBottomPosition - navBarHeight - heightAdjust, 150)
      );
    } else {
      setMenuMaxHeight(0);
    }
  }, [show, autoSetHeight]);

  const onChange = (option: FilterEntityOption) => {
    const selectedItemIndex = getSelectedItemIndex(option);
    if (selectedItemIndex >= 0) {
      setValue(
        name,
        selectedValues.filter(
          (item) =>
            String(item.name).toLowerCase() !==
            String(option.name).toLowerCase()
        ),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    } else {
      setValue(name, [...selectedValues, option], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const selectedValue = selectedValues.map((item) => item.name).join(', ');
  const placeHolder = 'Select all that apply';

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={() => {
          return (
            <>
              {label ? (
                <FormLabel label={label} required={required} error={error} />
              ) : null}
              <div>
                <Dropdown
                  className={classnames(styles.dropDown, className)}
                  show={show}
                  aria-label={name}
                  ref={dropDownRef}
                  drop={drop}
                >
                  <Dropdown.Toggle onClick={() => setShow(!show)}>
                    <div
                      className={classnames(
                        'text-truncate',
                        {
                          'text-light3': !selectedValue,
                        },
                        styles.dropDownTitle
                      )}
                      data-cy="dropdown-toggle"
                    >
                      {selectedValue || placeHolder}
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    ref={dropMenuDownRef}
                    className={styles.dropDownMenu}
                    style={{
                      ...(menuMaxHeight
                        ? { maxHeight: `${menuMaxHeight}px` }
                        : {}),
                    }}
                  >
                    {options.map((item) => {
                      const selected = getSelectedItemIndex(item) >= 0;
                      return (
                        <Dropdown.Item
                          key={item.id}
                          className={classnames(
                            styles.dropDownItem,
                            'd-flex align-items-center justify-content-between',
                            'text-wrap',
                            {
                              [`text-primary ${styles.selected}`]: selected,
                            }
                          )}
                          as="button"
                          type="button"
                          onClick={() => onChange(item)}
                        >
                          {item.name}
                          {selected ? <span>✓</span> : null}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
                <div className="mt-3">
                  {selectedValues.map((item) => (
                    <p key={item.name} className="text-light8 mb-1">
                      {item.name}
                    </p>
                  ))}
                </div>
              </div>
            </>
          );
        }}
      />
    </>
  );
};

export default MultiSelectFilter;
